import {
	HubTemplateManageListItemModel,
	deleteTemplates,
	list
} from "client/Api/Hub/Templates/hubTemplateManagement";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	AlertBar,
	DateString,
	DialogBackdropState,
	FormattedText,
	Pagination,
	SearchInputForm,
	Space,
	TableView,
	ToolbarAction,
	ToolbarRegularAction,
	Typography,
	useConfirmationDialog
} from "client/Components";
import { contains } from "client/Data/searching";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useForceRerender,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { HubManagementDialogContentLayout } from "../../HubManagementDialogContentLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { maxNumberOfHubTemplates, useCreateHubTemplateDialog } from "../../../Templates/CreateHubTemplateDialog";
import { useEditHubTemplateDialog } from "../../../Templates/EditHubTemplateDialog";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { useRenameTemplateDialog } from "./RenameTemplateDialog";
import styles from "./TemplateOverview.module.less";

export type TemplateOverviewProps = {
	onParentBackdropChange: (backdrop: DialogBackdropState) => void;
	kind: HubItemKind;
	isMobile: boolean;
	isResponsive: boolean;
};

export const TemplateOverview = (props: TemplateOverviewProps): ReactElement => {
	const {
		onParentBackdropChange,
		kind,
		isMobile,
		isResponsive
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [templates, setTemplates] = useState<readonly HubTemplateManageListItemModel[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [selectedItems, setSelectedItems] = useState<readonly HubTemplateManageListItemModel[]>([]);

	const [sortingState, setSortingState] = useState<DataSourceSortableState<HubTemplateManageListItemModel>>({
		direction: DataSourceSortDirection.Ascending,
		propertyName: "name"
	});

	const {
		currentItems,
		pagination
	} = useDataSource(
		templates,
		{
			searchTerm,
			sorting: sortingState,
			pagination: {
				currentPageNumber
			}
		}
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems,
			hasMemorization: true,
			allItems: templates
		}
	);

	const {
		sortingFor
	} = useSortHandler(sortingState, setSortingState);

	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [reload, dataVersion] = useForceRerender();

	const [createHubTemplateDialog, openCreateHubTemplateDialog] = useCreateHubTemplateDialog();
	const [editHubTemplateDialog, openEditHubTemplateDialog] = useEditHubTemplateDialog();
	const [renameDialog, openRenameDialog] = useRenameTemplateDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const canCreateNewTemplates: boolean = templates.length < maxNumberOfHubTemplates;

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchTemplates(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const templates: readonly HubTemplateManageListItemModel[] = await list(kind, signal);

					setTemplates(templates);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
					console.error(e);
				}
			}

			const cancellation = new AbortController();

			fetchTemplates(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			dataVersion,
			kind
		]
	);

	const newTemplateHandler = useCallback(
		async (): Promise<void> => {
			onParentBackdropChange("invisible");

			await openCreateHubTemplateDialog({ kind });

			onParentBackdropChange("visible");
			reload();
		},
		[
			kind,
			onParentBackdropChange,
			openCreateHubTemplateDialog,
			reload
		]
	);

	const editTemplateHandler = useCallback(
		async (id: number): Promise<void> => {
			onParentBackdropChange("invisible");

			await openEditHubTemplateDialog({ templateId: id });

			onParentBackdropChange("visible");
			reload();
		},
		[
			onParentBackdropChange,
			openEditHubTemplateDialog,
			reload
		]
	);

	const duplicateTemplateHandler = useCallback(
		async (id: number): Promise<void> => {
			onParentBackdropChange("invisible");

			await openCreateHubTemplateDialog({
				kind,
				basedOn: {
					templateId: id
				}
			});

			onParentBackdropChange("visible");
			reload();
		},
		[
			kind,
			onParentBackdropChange,
			openCreateHubTemplateDialog,
			reload
		]
	);

	const renameTemplateHandler = useCallback(
		async (id: number, currentName: string): Promise<void> => {
			await openRenameDialog({id, currentName});
			reload();
		},
		[
			openRenameDialog,
			reload
		]
	);

	const deleteTemplatesHandler = useCallback(
		async (ids: readonly number[]): Promise<void> => {
			const confirmation: TryResult<void> = await openConfirmationDialog({
				title: localize(
					ids.length === 1
						? "_AreYouSureYouWantToDeleteThisTemplate"
						: "_AreYouSureYouWantToDeleteTheseTemplates"
				),
				confirmText: localize("_Delete"),
				trackingId: "delete-templates-confirmation-dialog"
			});

			if (!confirmation.wasSuccessful) {
				return;
			}

			try {
				setIsBusy(true);
				await deleteTemplates(ids, globalCancellation);
				setIsBusy(false);
				reload();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog,
			reload
		]
	);

	const selectionActions = useMemo(
		(): readonly TemplateAction[] => {
			return getSelectionActions(
				() => setSelectedItems([])
			);
		},
		[]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<TemplateActionContext> => {
			return getMainAction(
				newTemplateHandler
			);
		},
		[newTemplateHandler]
	);

	const contextualActions = useMemo(
		(): readonly TemplateAction[] => {
			return getContextualActions(
				editTemplateHandler,
				duplicateTemplateHandler,
				renameTemplateHandler,
				deleteTemplatesHandler
			);
		},
		[
			deleteTemplatesHandler,
			duplicateTemplateHandler,
			editTemplateHandler,
			renameTemplateHandler
		]
	);

	const hasSelection: boolean = selectedItems.length > 0;

	const actions = useMemo(
		(): readonly TemplateAction[] => {
			return buildActionsForTopBar<TemplateActionContext, TemplateAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			hasSelection,
			isResponsive
		]
	);

	const actionContext = useMemo(
		(): TemplateActionContext => {
			return {
				selectedItems,
				canCreateNewTemplates,
				isMobile
			};
		},
		[
			canCreateNewTemplates,
			isMobile,
			selectedItems
		]
	);

	const getDisplayName = (user: UserIdentityModel | null): string => {
		if (!user) {
			return "";
		}

		const {
			computedDisplayName
		} = getComputedDisplayName({
			firstName: user.firstName,
			lastName: user.lastName
		});

		return computedDisplayName;
	};

	return (
		<>
			<HubManagementDialogContentLayout
				title={localize("_Templates")}
				isMobile={isMobile}
				isResponsive={isResponsive}
				actions={actions}
				actionContext={actionContext}
				hasSelection={hasSelection}
				trackingId="templates-overview"
				isBusy={isBusy}
			>
				{!canCreateNewTemplates && (
					<div className={styles.alertBarContainer}>
						<AlertBar
							kind="info"
							isInline
							isDismissable={false}
						>
							<FormattedText
								text={localize("_HubTemplatesMaxCountErrorMessage")}
								thingsToReplace={[
									{
										what: "{{MaxCount}}",
										filler: maxNumberOfHubTemplates
									}
								]}
							/>
							<Space />
							{localize("_RemoveExistingTemplatesToCreateNewOnes")}
						</AlertBar>
					</div>
				)}
				<HubManagementDialogContentLayout.Filter>
					<SearchInputForm
						trackingId="template-management-search-form"
						onChange={setSearchTerm}
						placeholder={
							<FormattedText
								text={localize("_FilterByProperties")}
								thingsToReplace={[
									{
										what: "{{Properties}}",
										filler: (
											<Typography
												weight="bold"
												isItalic
											>
												{localize("_Name")}
											</Typography>
										)
									}
								]}
							/>
						}
						height="large"
						icon="filter"
					/>
				</HubManagementDialogContentLayout.Filter>
				<HubManagementDialogContentLayout.Table>
					<TableView
						items={currentItems}
						itemKey="id"
						height="stretch"
						multiSelectable={multiSelection}
						noResultConfig={{
							icon: "file-lines",
							title: localize("_NoTemplatesYet"),
							message: localize("_ThereAreNoTemplatesYet")
						}}
						columns={[
						{
							title: localize("_Name"),
							trackingId: "name",
							width: 270,
							sortable: sortingFor("name")
						},
						{
							title: localize("_DateCreated"),
							trackingId: "created-on",
							width: 150,
							sortable: sortingFor("createdOn")
						},
						{
							title: localize("_DateModified"),
							trackingId: "modified-on",
							width: 150,
							sortable: sortingFor("modifiedOn")
						},
						{
							title: localize("_CreatedBy"),
							trackingId: "created-by",
						},
						{
							title: localize("_ModifiedBy"),
							trackingId: "modified-by"
						}
						]}
						trackingId="template-management-grid"
					>
						{(template) => (
							<TableView.Row
								trackingId={template.id.toString()}
								selectable={selectionFor(template)}
								onExecute={() => editTemplateHandler(template.id)}
							>
								<TableView.Cell tooltip={template.name}>
									{template.name}
								</TableView.Cell>
								<TableView.Cell>
									<DateString
										value={template.createdOn}
										format="RelativeDay"
										timezone={userTimezone}
									/>
								</TableView.Cell>
								<TableView.Cell>
									<DateString
										value={template.modifiedOn}
										format="RelativeDay"
										timezone={userTimezone}
									/>
								</TableView.Cell>
								<TableView.Cell tooltip={getDisplayName(template.createdBy)}>
									{getDisplayName(template.createdBy)}
								</TableView.Cell>
								<TableView.Cell tooltip={getDisplayName(template.modifiedBy)}>
									{getDisplayName(template.modifiedBy)}
								</TableView.Cell>
							</TableView.Row>
						)}
					</TableView>
					<Pagination
						{...pagination}
						onPageNumberChanged={setCurrentPageNumber}
						trackingId="template-management-pagination"
					/>
				</HubManagementDialogContentLayout.Table>
				{isResponsive && mainAction.canExecute(actionContext) && (
					<HubManagementDialogContentLayout.MainButton
						onClick={() => mainAction.execute(actionContext)}
						trackingId={mainAction.name}
					/>
				)}
			</HubManagementDialogContentLayout>
			{createHubTemplateDialog}
			{editHubTemplateDialog}
			{renameDialog}
			{confirmationDialog}
		</>
	);
};
TemplateOverview.displayName = "TemplateOverview";

const useDataSource = createLocalDataSource<HubTemplateManageListItemModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.name, searchTerm)
		},
		hasPagination: true
	})
	.getHook();

type TemplateActionContext = {
	selectedItems: readonly HubTemplateManageListItemModel[];
	canCreateNewTemplates: boolean;
	isMobile: boolean;
};

type TemplateAction = ToolbarAction<TemplateActionContext>;

function getSelectionActions(
	deselectHandler: () => void
): readonly TemplateAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedItems),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedItems.length > 0,
			execute: () => deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedItems.length > 0
		}
	];
}

function getMainAction(
	newTemplateHandler: () => void
): ToolbarRegularAction<TemplateActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		icon: "plus",
		canExecute: (ctx) => ctx.canCreateNewTemplates,
		execute: () => newTemplateHandler(),
		isPrimary: true
	};
}

function getContextualActions(
	editTemplateHandler: (id: number) => void,
	duplicateTemplateHandler: (id: number) => void,
	renameTemplateHandler: (id: number, currentName: string) => void,
	deleteTemplatesHandler: (ids: readonly number[]) => void
): readonly TemplateAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			icon: "pen",
			canExecute: (ctx) => ctx.selectedItems.length === 1,
			execute: (ctx) => editTemplateHandler(ctx.selectedItems[0].id)
		},
		{
			kind: ActionKind.Regular,
			name: "duplicate",
			title: localize("_Duplicate"),
			icon: "copy",
			canExecute: (ctx) => ctx.canCreateNewTemplates && ctx.selectedItems.length === 1,
			execute: (ctx) => duplicateTemplateHandler(ctx.selectedItems[0].id)
		},
		{
			kind: ActionKind.Regular,
			name: "rename",
			title: localize("_Rename"),
			icon: "text",
			canExecute: (ctx) => ctx.selectedItems.length === 1,
			execute: (ctx) => {
				const item: HubTemplateManageListItemModel = ctx.selectedItems[0];
				renameTemplateHandler(item.id, item.name);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (ctx) => ctx.selectedItems.length > 0,
			execute: (ctx) => deleteTemplatesHandler(ctx.selectedItems.map(i => i.id))
		}
	];
}